// Log extraction accuracy data for every confirmed invoice.
// One header row + one row per COGS line item.
// Failures are silently caught — logging never blocks confirms.

export async function logExtraction(supabase, {
  orgId, invoiceId, confirmPath,
  extracted,    // raw OCR result (extracted_data)
  matched,      // final matched/confirmed line items
  vendor,       // confirmed vendor name
  invNumber,    // confirmed invoice number
  invDate,      // confirmed date
  grandTotal,   // confirmed total
  lineEdits,    // {[lineIdx]: {[field]: {original, current}}} from InvoiceReview
  skippedLines, // Set or array of skipped line indices
  extractionMs, // OCR duration in ms
  docSource,    // 'pdf_text', 'pdf_image', 'photo'
}) {
  if (!orgId || !invoiceId) return;
  try {
    const skipped = new Set(skippedLines || []);
    const ocrItems = extracted?.line_items || [];
    const rows = [];

    // Header row: invoice-level field accuracy
    const headerCorrected = [];
    const ocrVendor = (extracted?.raw_vendor || extracted?.vendor || '').trim();
    const ocrInvNum = (extracted?.invoice_number || '').trim();
    const ocrDate = extracted?.invoice_date || null;
    const ocrTotal = extracted?.grand_total ?? null;
    if (ocrVendor && vendor && ocrVendor.toLowerCase() !== vendor.toLowerCase()) headerCorrected.push('vendor');
    if (ocrInvNum && invNumber && ocrInvNum !== invNumber) headerCorrected.push('invoice_number');
    if (ocrDate && invDate && ocrDate !== invDate) headerCorrected.push('date');
    if (ocrTotal != null && grandTotal != null && Math.abs(Number(ocrTotal) - Number(grandTotal)) > 0.01) headerCorrected.push('total');

    rows.push({
      org_id: orgId, invoice_id: invoiceId, confirm_path: confirmPath,
      vendor, invoice_number: invNumber, invoice_date: invDate,
      row_type: 'header', line_index: null, item_number: null,
      extraction_ms: extractionMs || null, doc_source: docSource || null,
      ocr_vendor: ocrVendor || null, ocr_invoice_number: ocrInvNum || null,
      ocr_date: ocrDate, ocr_total: ocrTotal,
      final_vendor: vendor, final_invoice_number: invNumber, final_date: invDate, final_total: grandTotal,
      resolution: 'header', was_corrected: headerCorrected.length > 0,
      corrected_fields: headerCorrected.length > 0 ? headerCorrected : null,
    });

    // Line rows
    for (let i = 0; i < (matched || []).length; i++) {
      const m = matched[i];
      const ocr = ocrItems[i] || {};
      if (!m.amount && m.amount !== 0) continue; // skip empty

      // Determine resolution
      let resolution = 'auto_match';
      if (skipped.has(i)) resolution = 'skipped';
      else if (!m.matched_ingredient_id && m.match_method === 'none') resolution = 'new_item';
      else if (m.match_method === 'manual') resolution = 'manual_link';
      else if (m.matched_ingredient_id) resolution = 'auto_match';
      else resolution = 'new_item';

      // Detect corrections from lineEdits
      const edits = lineEdits?.[i] || {};
      const corrected = [];
      if (edits.qty_shipped) corrected.push('qty_shipped');
      if (edits.unit_price) corrected.push('unit_price');
      if (edits.amount) corrected.push('amount');
      // Description/category corrections detected by comparing OCR vs final
      if (ocr.description && m.description && ocr.description !== m.description) corrected.push('description');
      if (ocr.pack_qty && m.pack_qty && ocr.pack_qty !== m.pack_qty) corrected.push('pack_qty');

      rows.push({
        org_id: orgId, invoice_id: invoiceId, confirm_path: confirmPath,
        vendor, invoice_number: invNumber, invoice_date: invDate,
        row_type: 'line', line_index: i, item_number: m.item_number || ocr.item_number || null,
        extraction_ms: null, doc_source: null,
        ocr_description: ocr.description || null,
        ocr_pack_size: ocr.pack_size || null,
        ocr_pack_qty: ocr.pack_qty || null,
        ocr_unit_price: ocr.unit_price ?? null,
        ocr_amount: ocr.amount ?? null,
        ocr_qty_shipped: ocr.qty_shipped ?? null,
        ocr_category: ocr.category || null,
        final_description: m.description || null,
        final_pack_qty: m.pack_qty || null,
        final_unit_price: edits.unit_price ? edits.unit_price.current : (m.unit_price ?? null),
        final_amount: edits.amount ? edits.amount.current : (m.amount ?? null),
        final_qty_shipped: edits.qty_shipped ? edits.qty_shipped.current : (m.qty_shipped ?? null),
        final_category: m.category || null,
        resolution,
        matched_ingredient_id: m.matched_ingredient_id || null,
        match_method: m.match_method || null,
        match_confidence: m.match_confidence ?? null,
        was_corrected: corrected.length > 0,
        corrected_fields: corrected.length > 0 ? corrected : null,
      });
    }

    // Batch insert (200 at a time)
    for (let i = 0; i < rows.length; i += 200) {
      await supabase.from('extraction_log').insert(rows.slice(i, i + 200));
    }
  } catch (e) {
    console.error('[extractionLog] Failed to log extraction:', e.message);
  }
}
